import { sortBy, uniqBy } from "lodash-es";
import { ItemType } from "../../../clients/graphqlTypes.js";
import { canHaveCreateInput as _canHaveCreateInput } from "../doc-items/tree-helpers.js";
import { getDefaultCreatableSubItemTypes } from "../doc-items/constraints.js";
import { getParentItem } from "../../trees/helpers.js";
import { ROOT_ITEM_ID } from "../../trees-virtual/helpers.js";
import type { AdditionalItemInput } from "../../trees-virtual/types.js";
import type { DocDataGrid_ItemFragment } from "./DocDataGrid.generated.js";
import type { DocDataGridRow } from "./types.js";

// ---------------------------------------------------------------------------
// Path building helpers
// ---------------------------------------------------------------------------

function buildItemPath<T extends DocDataGrid_ItemFragment>(
  item: T,
  itemsById: Map<string, T>,
  rootItemId: string | undefined,
  showRootItem: boolean
): string[] {
  if (
    !item.parentId ||
    item.parentId === ROOT_ITEM_ID ||
    item.parentId === rootItemId
  ) {
    return showRootItem && rootItemId ? [rootItemId, item.id] : [item.id];
  }
  const parent = itemsById.get(item.parentId);
  if (!parent)
    return showRootItem && rootItemId ? [rootItemId, item.id] : [item.id];
  return [
    ...buildItemPath(parent, itemsById, rootItemId, showRootItem),
    item.id,
  ];
}

// ---------------------------------------------------------------------------
// buildDocDataGridRows
// ---------------------------------------------------------------------------

export function buildDocDataGridRows<T extends DocDataGrid_ItemFragment>(
  items: T[],
  rootItem: T | undefined,
  selectedItem: T | null,
  additionalItemInput: AdditionalItemInput | null | undefined,
  enableCreating: boolean,
  documentItemTypes: ItemType[],
  filterItem?: (item: T) => boolean,
  shouldRenderCreateInput?: (item: T) => boolean,
  showRootItem: boolean = true
): DocDataGridRow<T>[] {
  const rootItemId = rootItem?.id;

  const selectedItemParent =
    selectedItem && selectedItem.parentId !== null
      ? getParentItem(selectedItem, items)
      : null;

  const additionalItem = additionalItemInput
    ? (items.find(i => i.id === additionalItemInput.itemId) ?? null)
    : null;

  const additionalItemParent =
    additionalItem && additionalItem.parentId !== null
      ? getParentItem(additionalItem, items)
      : null;

  // Find last children by rank for input row rank placement
  const {
    lastRootItemChild,
    lastSelectedItemChild,
    lastSelectedItemParentChild,
  } = items.reduce(
    (acc, item) => {
      if (rootItemId && item.parentId === rootItemId) {
        if (!acc.lastRootItemChild || item.rank > acc.lastRootItemChild.rank)
          acc.lastRootItemChild = item;
      }
      if (selectedItem && item.parentId === selectedItem.id) {
        if (
          !acc.lastSelectedItemChild ||
          item.rank > acc.lastSelectedItemChild.rank
        )
          acc.lastSelectedItemChild = item;
      }
      if (selectedItemParent && item.parentId === selectedItemParent.id) {
        if (
          !acc.lastSelectedItemParentChild ||
          item.rank > acc.lastSelectedItemParentChild.rank
        )
          acc.lastSelectedItemParentChild = item;
      }
      return acc;
    },
    {
      lastRootItemChild: null as T | null,
      lastSelectedItemChild: null as T | null,
      lastSelectedItemParentChild: null as T | null,
    }
  );

  const itemsById = new Map(items.map(item => [item.id, item]));

  // Pre-compute children grouped by parentId for O(1) lookup
  const childrenByParentId = new Map<string, T[]>();
  for (const item of items) {
    if (item.parentId) {
      let children = childrenByParentId.get(item.parentId);
      if (!children) {
        children = [];
        childrenByParentId.set(item.parentId, children);
      }
      children.push(item);
    }
  }

  // Build input rows (4 cases mirroring getTreeFromItems in trees-virtual/helpers.ts)
  type InputSpec = {
    inputParentId: string;
    inputRank: number;
    isAdditionalInput?: boolean;
  };

  const inputSpecs: InputSpec[] = enableCreating
    ? uniqBy(
        [
          // Case 4 (additional item input from context menu, above or below a
          // specific item) is listed first so it wins `uniqBy` on a rank
          // collision with the Case 3 sibling-create input (e.g. "add below"
          // the last child), preserving `isAdditionalInput`.
          ...(additionalItemInput &&
          additionalItem &&
          additionalItemParent &&
          !(
            "deletedAt" in additionalItemParent &&
            additionalItemParent.deletedAt
          ) &&
          additionalItemParent.id !== ROOT_ITEM_ID &&
          shouldRenderCreateInput?.(additionalItemParent) !== false &&
          _canHaveCreateInput(
            additionalItemParent.type,
            documentItemTypes,
            Boolean(additionalItemParent.linkedQuoteTemplate)
          )
            ? [
                {
                  inputParentId: additionalItemParent.id,
                  inputRank:
                    additionalItem.rank +
                    (additionalItemInput.position === "below" ? 0.5 : -0.5),
                  isAdditionalInput: true,
                },
              ]
            : []),

          // Case 1: root item input (create child of root)
          ...(rootItem &&
          !("deletedAt" in rootItem && rootItem.deletedAt) &&
          rootItem.id !== ROOT_ITEM_ID &&
          shouldRenderCreateInput?.(rootItem) !== false &&
          _canHaveCreateInput(
            rootItem.type,
            documentItemTypes,
            Boolean(rootItem.linkedQuoteTemplate)
          )
            ? [
                {
                  inputParentId: rootItem.id,
                  inputRank: (lastRootItemChild?.rank ?? 0) + 0.5,
                },
              ]
            : []),

          // Case 2: selected item input (create children of selected)
          ...(selectedItem &&
          !("deletedAt" in selectedItem && selectedItem.deletedAt) &&
          selectedItem.id !== rootItemId &&
          shouldRenderCreateInput?.(selectedItem) !== false &&
          _canHaveCreateInput(
            selectedItem.type,
            documentItemTypes,
            Boolean(selectedItem.linkedQuoteTemplate)
          )
            ? [
                {
                  inputParentId: selectedItem.id,
                  inputRank: (lastSelectedItemChild?.rank ?? 0) + 0.5,
                },
              ]
            : []),

          // Case 3: selected item's parent input (create siblings of selected)
          ...(selectedItemParent &&
          !(
            "deletedAt" in selectedItemParent && selectedItemParent.deletedAt
          ) &&
          selectedItemParent.id !== rootItemId &&
          shouldRenderCreateInput?.(selectedItemParent) !== false &&
          _canHaveCreateInput(
            selectedItemParent.type,
            documentItemTypes,
            Boolean(selectedItemParent.linkedQuoteTemplate)
          )
            ? [
                {
                  inputParentId: selectedItemParent.id,
                  inputRank: (lastSelectedItemParentChild?.rank ?? 0) + 0.5,
                },
              ]
            : []),
        ],
        (spec: InputSpec) => `${spec.inputParentId}/${spec.inputRank}` // deduplicate by parentId & rank
      )
    : [];

  const inputRows: DocDataGridRow<T>[] = inputSpecs.map(
    ({ inputParentId, inputRank, isAdditionalInput }) => {
      const id = `input:${inputParentId}:${inputRank}`;
      const parentItem = itemsById.get(inputParentId) ?? null;

      // Compute path: if parent is root item, this input is top-level (or nested under root row)
      let path: string[];
      if (inputParentId === rootItemId) {
        path = showRootItem && rootItemId ? [rootItemId, id] : [id];
      } else {
        const parentPath = parentItem
          ? buildItemPath(parentItem, itemsById, rootItemId, showRootItem)
          : [];
        path = [...parentPath, id];
      }

      return {
        id,
        kind: "input" as const,
        // store parent item so InputComponent can access it (mirrors VirtualTreeItem pattern)
        item: parentItem,
        parentItem: parentItem?.parentId
          ? (itemsById.get(parentItem.parentId) ?? null)
          : null,
        itemChildren: childrenByParentId.get(inputParentId) ?? [],
        inputMeta: additionalItemInput ?? null,
        isAdditionalInput,
        path,
        depth: path.length - 1,
        rank: inputRank,
        parentId: inputParentId,
        type: null,
      };
    }
  );

  // Build regular item rows (exclude root item)
  const itemRows: DocDataGridRow<T>[] = items
    .filter(item => {
      if (item.isRootItem) return false;
      if (filterItem?.(item) === false) return false;
      return true;
    })
    .map(item => {
      const path = buildItemPath(item, itemsById, rootItemId, showRootItem);
      return {
        id: item.id,
        kind: "item" as const,
        item,
        parentItem: item.parentId
          ? (itemsById.get(item.parentId) ?? null)
          : null,
        itemChildren: childrenByParentId.get(item.id) ?? [],
        inputMeta: null,
        path,
        depth: path.length - 1,
        rank: item.rank,
        parentId: item.parentId ?? null,
        type: item.type,
        __reorder__: `${item.pathForPdf} ${item.title}`,
      };
    });

  // Optionally prepend root item as the top-level row
  const rootRow: DocDataGridRow<T>[] =
    showRootItem && rootItem && rootItemId
      ? [
          {
            id: rootItemId,
            kind: "item" as const,
            item: rootItem,
            parentItem: null,
            itemChildren: childrenByParentId.get(rootItemId) ?? [],
            inputMeta: null,
            path: [rootItemId],
            depth: 0,
            rank: 0,
            parentId: null,
            type: rootItem.type,
            __reorder__: `${rootItem.pathForPdf} ${rootItem.title}`,
          },
        ]
      : [];

  return sortBy([...rootRow, ...itemRows, ...inputRows], r => r.rank);
}

// ---------------------------------------------------------------------------
// canDropDocDataGridRow
// ---------------------------------------------------------------------------

export type DropPosition = "above" | "below" | "inside";

export interface CanDropParams<T extends DocDataGrid_ItemFragment> {
  sourceRowId: string;
  targetRowId: string;
  dropPosition: DropPosition;
  /** ID of the row visually before the target (from DataGrid context prevNode) */
  prevRowId: string | null;
  /** ID of the row visually after the target (from DataGrid context nextNode) */
  nextRowId: string | null;
  rows: DocDataGridRow<T>[];
  documentItemTypes: ItemType[];
  isLoading: boolean;
}

/**
 * Pure canDrop predicate for DocDataGrid row reordering.
 * Implements the same six constraints as VirtualTableTree lines 437–491.
 */
export function canDropDocDataGridRow<T extends DocDataGrid_ItemFragment>({
  sourceRowId,
  targetRowId,
  dropPosition,
  prevRowId,
  nextRowId,
  rows,
  documentItemTypes,
  isLoading,
}: CanDropParams<T>): boolean {
  // Constraint 1: block while mutation is loading
  if (isLoading) return false;

  const rowsById = new Map(rows.map(r => [r.id, r]));
  const sourceRow = rowsById.get(sourceRowId);
  const targetRow = rowsById.get(targetRowId);
  if (!sourceRow || !targetRow) return false;

  // Determine the proposed parent row
  let proposedParentRow: DocDataGridRow<T> | undefined;
  if (dropPosition === "inside") {
    proposedParentRow = targetRow;
  } else {
    // above/below: parent is target's parent
    proposedParentRow = targetRow.parentId
      ? rowsById.get(targetRow.parentId)
      : undefined;
  }

  // Block dropping above/below root item (no siblings allowed)
  if (targetRow.item?.isRootItem && dropPosition !== "inside") return false;

  // Constraint 4: proposed parent is an input row
  if (proposedParentRow?.kind === "input") return false;

  // Constraint 5: proposed parent is synthetic root
  if (proposedParentRow?.parentId === ROOT_ITEM_ID) {
    // proposedParentRow is the root item itself — not in rows but let through
    // If we somehow get a row with ROOT_ITEM_ID parentId, reject
  }
  if (targetRow.parentId === ROOT_ITEM_ID && dropPosition !== "inside") {
    return false;
  }

  // Constraint 2: proposed parent has linkedQuoteTemplate
  if (proposedParentRow?.item?.linkedQuoteTemplate) return false;

  // Constraint 3: parent type doesn't accept source item's type
  if (proposedParentRow?.type && sourceRow.type) {
    const creatableTypes = getDefaultCreatableSubItemTypes(
      proposedParentRow.type,
      documentItemTypes
    );
    if (!creatableTypes.includes(sourceRow.type)) return false;
  }

  // Constraint 6: drop would place node adjacent to an input row
  if (dropPosition === "above") {
    // Result: ..., [dragged], targetRow, ...
    // The row immediately after dragged would be targetRow — reject if input
    if (targetRow.kind === "input") return false;
  }
  if (dropPosition === "below") {
    // Result: ..., targetRow, [dragged], nextRow, ...
    const nextRow = nextRowId ? rowsById.get(nextRowId) : undefined;
    if (nextRow?.kind === "input") return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// computeDropPlacement
// ---------------------------------------------------------------------------

export interface DropPlacement {
  parentId: string;
  rank: number;
}

export interface ComputeDropPlacementParams<
  T extends DocDataGrid_ItemFragment,
> {
  sourceRowId: string;
  targetRow: DocDataGridRow<T>;
  dropPosition: DropPosition;
  rows: DocDataGridRow<T>[];
  documentItemTypes: ItemType[];
  /** Expanded row IDs, matching the grid's expansion source. `undefined` means all expanded. */
  expandedRowIds: string[] | undefined;
}

/** First child position; the backend rank-shifts existing children down. */
const FIRST_CHILD_RANK = 1;

/**
 * Pure computation of the new parent + rank for a valid DocDataGrid drop.
 *
 * - `inside` → first child of the target.
 * - `above`  → sibling immediately before the target (unchanged).
 * - `below`  → first child of the target when it has children, is expanded, and
 *              nesting is valid; otherwise sibling immediately after the target.
 */
export function computeDropPlacement<T extends DocDataGrid_ItemFragment>({
  sourceRowId,
  targetRow,
  dropPosition,
  rows,
  documentItemTypes,
  expandedRowIds,
}: ComputeDropPlacementParams<T>): DropPlacement {
  if (dropPosition === "inside") {
    return { parentId: targetRow.id, rank: FIRST_CHILD_RANK };
  }

  if (dropPosition === "above") {
    return { parentId: targetRow.parentId ?? "", rank: targetRow.rank };
  }

  // dropPosition === "below": nest as first child when the target is an
  // expanded parent that can accept the dragged item; otherwise sibling-after.
  const targetHasChildren = rows.some(
    r => r.parentId === targetRow.id && r.kind === "item"
  );
  const targetExpanded =
    !expandedRowIds ||
    targetRow.item?.isRootItem === true ||
    expandedRowIds.includes(targetRow.id);
  const canNest =
    targetHasChildren &&
    targetExpanded &&
    canDropDocDataGridRow({
      sourceRowId,
      targetRowId: targetRow.id,
      dropPosition: "inside",
      // prev/next only matter for the input-adjacency check on above/below.
      prevRowId: null,
      nextRowId: null,
      rows,
      documentItemTypes,
      isLoading: false,
    });

  if (canNest) {
    return { parentId: targetRow.id, rank: FIRST_CHILD_RANK };
  }

  return { parentId: targetRow.parentId ?? "", rank: targetRow.rank + 1 };
}
